import {
  createSlice,
  createAsyncThunk,
  isPending,
  isFulfilled,
  isRejected,
} from "@reduxjs/toolkit";
import http from "../../utils/axios";

// Fetch all states
export const fetchStates = createAsyncThunk(
  "states/fetchStates",
  async ({ page = 1, per_page = 15, status, search } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("per_page", per_page);
    if (status !== undefined) params.append("status", status);
    if (search) params.append("search", search);
    
    const res = await http.get(`/dashboard/states?${params.toString()}`);
    return res;
  }
);

// Create state
export const createState = createAsyncThunk(
  "states/createState",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await http.post("/dashboard/states", payload);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to create state"
      );
    }
  }
);

// Fetch state by UUID
export const fetchStateById = createAsyncThunk(
  "states/fetchStateById",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/states/${uuid}`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch state details"
      );
    }
  }
);

// Update state
export const updateState = createAsyncThunk(
  "states/updateState",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const res = await http.put(`/dashboard/states/${uuid}`, data);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to update state"
      );
    }
  }
);

// Delete state
export const deleteState = createAsyncThunk(
  "states/deleteState",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.delete(`/dashboard/states/${uuid}`);
      return { uuid, data: res };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to delete state"
      );
    }
  }
);

// Toggle state status
export const toggleStateStatus = createAsyncThunk(
  "states/toggleStateStatus",
  async (uuid, { rejectWithValue }) => {
    try {
      const res = await http.patch(`/dashboard/states/${uuid}/toggle-status`);
      return res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Failed to toggle state status"
      );
    }
  }
);

const statesSlice = createSlice({
  name: "states",
  initialState: {
    states: [],
    pagination: null,
    state: null,
    stateCreateStatus: "idle",
    stateUpdateStatus: "idle",
    stateDeleteStatus: "idle",
    stateFetchStatus: "idle",
    stateStatusChangeStatus: "idle",
    loading: false,
    error: null,
  },
  reducers: {
    resetStateStatuses: (state) => {
      state.stateCreateStatus = "idle";
      state.stateUpdateStatus = "idle";
      state.stateDeleteStatus = "idle";
      state.stateStatusChangeStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch states
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.states = action.payload.data;
        state.pagination = action.payload.pagination;
      })

      // Create state
      .addCase(createState.pending, (state) => {
        state.stateCreateStatus = "loading";
      })
      .addCase(createState.fulfilled, (state, action) => {
        state.stateCreateStatus = "success";
        state.state = action.payload.data;
      })
      .addCase(createState.rejected, (state) => {
        state.stateCreateStatus = "failed";
      })

      // Fetch state by ID
      .addCase(fetchStateById.pending, (state) => {
        state.stateFetchStatus = "loading";
      })
      .addCase(fetchStateById.fulfilled, (state, action) => {
        state.stateFetchStatus = "success";
        state.state = action.payload.data;
      })
      .addCase(fetchStateById.rejected, (state) => {
        state.stateFetchStatus = "failed";
        state.state = null;
      })

      // Update state
      .addCase(updateState.pending, (state) => {
        state.stateUpdateStatus = "loading";
      })
      .addCase(updateState.fulfilled, (state, action) => {
        state.stateUpdateStatus = "success";
        const index = state.states.findIndex(
          (st) => st.uuid === action.meta.arg.uuid
        );
        if (index !== -1) {
          state.states[index] = action.payload.data;
        }
        state.state = action.payload.data;
      })
      .addCase(updateState.rejected, (state) => {
        state.stateUpdateStatus = "failed";
      })

      // Delete state
      .addCase(deleteState.pending, (state) => {
        state.stateDeleteStatus = "loading";
      })
      .addCase(deleteState.fulfilled, (state, action) => {
        state.stateDeleteStatus = "success";
        state.states = state.states.filter(
          (st) => st.uuid !== action.payload.uuid
        );
      })
      .addCase(deleteState.rejected, (state) => {
        state.stateDeleteStatus = "failed";
      })

      // Toggle status
      .addCase(toggleStateStatus.pending, (state) => {
        state.stateStatusChangeStatus = "loading";
      })
      .addCase(toggleStateStatus.fulfilled, (state, action) => {
        state.stateStatusChangeStatus = "success";
        const index = state.states.findIndex(
          (st) => st.uuid === action.meta.arg
        );
        if (index !== -1) {
          state.states[index] = action.payload.data;
        }
      })
      .addCase(toggleStateStatus.rejected, (state) => {
        state.stateStatusChangeStatus = "failed";
      })

      .addMatcher(isPending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addMatcher(isFulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(isRejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { resetStateStatuses } = statesSlice.actions;
export default statesSlice.reducer;
